/**
 * @fileoverview Yellow Pages searcher for category-based business search.
 * @module searchers/YellowSearcher
 */

import { DbSearcher } from './DbSearcher.js';
import { setupSelectSearchInput } from '../utils/InputHandlerFactory.js';
import { buildSearchResult, updateSearchPackWithSearchResult, escapeHtml, getOwnValue } from '../utils/jrfuncs.js';
import { calcCategorySegments } from '../utils/nynfuncs.js';
import { SEARCH_TYPES, DOM_ELEMENTS } from '../constants.js';

const { ID_YELLOW_SEE_ALSO, CLASS_YELLOW_SEE_ALSO_LINK, CLASS_SEE_ALSO_CONTENT } = DOM_ELEMENTS;


// Sentinel prefix used as the <option> value for SPECIAL group entries.
// Picked to be unmistakable (no real pcatg value can collide with this).
const SPECIAL_QUERY_PREFIX = '__special__:';




/**
 * Manages Yellow Pages search functionality.
 * Uses a select dropdown for category-based searching with hierarchical
 * categories (parent > child format) and "See Also" related categories.
 * @class
 * @extends DbSearcher
 */
export class YellowSearcher extends DbSearcher {
    /**
     * Creates a new YellowSearcher instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.seeAlsoContainerId] - ID of the "See Also" container
     */
    constructor(mapApp, options = {}) {
        super(mapApp, {
            searcherName: SEARCH_TYPES.YELLOW,
            // Override inputId since yellow uses a select element
            inputId: DOM_ELEMENTS.ID_YELLOW_SEARCH_SELECT,
            //
            showAds: true,
            enableActionBarSave: true,
            enableActionBarEdit: true,
            enableActionBarRead: true,
            searchMethod: YellowSearcher.prototype.doYellowSearchForPcatg,
            searchMethodOptions: { fields: ['pcatg'] },
            emptyMessage: 'Select a category to browse.',
            listerOptions: {
                contentsLabel: 'yellow pages search results',
                supportsIndent: true,
            },
            //
            ...options
        });
        this.seeAlsoContainerId = options.seeAlsoContainerId ?? ID_YELLOW_SEE_ALSO;
    }




    // ==========================================
    // Extra work for handling a dropdown instead of a normal input field
    // ==========================================



    /**
     * Sets up input handlers for the category select element.
     * Overrides base class to handle select element instead of text input.
     * @override
     */
    setupInputHandlers() {
        const select = document.getElementById(this.inputId);
        const clearButton = document.getElementById(this.clearButtonId);

        if (select) {
            // Store the cleanup function: Searcher.destroy() calls it, since the select is static page DOM
            this._inputHandlerCleanup = setupSelectSearchInput(
                select,
                clearButton,
                (value, isEnterKey) => this.performSearch({query: value, queryOriginal: value, isEnterKey: isEnterKey, flagUserInitiatedThisSearch: true})
            );
        }
    }


    /**
     * Hook called after search results are updated.
     * Updates the "See Also" section with related categories. SPECIAL list
     * queries are not pcatg-driven, so the See Also panel is cleared.
     * @param {Object} searchPack - The search pack with query (category name) and options
     * @param {Array} entries - The result entries
     * @override
     */
    onSearchComplete(searchPack, entries) {
        if (this.isSpecialQuery(searchPack.query)) {
            this.updateSeeAlso("");
            return;
        }
        this.updateSeeAlso(searchPack.queryOriginal);
    }

    /**
     * Hook called when search is cleared.
     * Clears the "See Also" section.
     * @override
     */
    onSearchCleared() {
        this.updateSeeAlso("");
        super.onSearchCleared();
    }




    // ==========================================
    // See Also helpers
    // ==========================================

    /**
     * Update the "See Also" section with related categories
     * @param {string} category - The selected category
     */
    updateSeeAlso(category) {
        // console.log(`IN updateSeeAlso with ${category}.`)
        if (category) {
            // the category comes from stored/imported query text, so own keys only (getOwnValue)
            const pcatgdata = getOwnValue(this.mapApp.getPcatgdict(), category);
            const seealso = getOwnValue(pcatgdata, 'seealso');
            if (Array.isArray(seealso) && seealso.length > 0) {
                // Build the HTML with links
                const links = seealso.map(cat => {
                    const catHtml = escapeHtml(cat);
                    return `<a href="#" class="${CLASS_YELLOW_SEE_ALSO_LINK}" data-category="${catHtml}">${catHtml}</a>`;
                }).join(' • ');
                this.setSeeAlsoContents(`<small class="text-muted">See also: ${links}</small>`);
                return;
            }
        }
        // Clear see also
        this.clearSeeAlsoContents();
    }


    /**
     * Clears the "See Also" section and hides the container.
     * @private
     */
    clearSeeAlsoContents() {
        const seeAlsoDiv = document.getElementById(ID_YELLOW_SEE_ALSO);
        const seeAlsoContent = seeAlsoDiv?.querySelector('.' + CLASS_SEE_ALSO_CONTENT);
        if (!seeAlsoDiv || !seeAlsoContent) return;

        seeAlsoDiv.style.display = 'none';
    }


    /**
     * Set the See Also section contents
     * @param {string} htmlContents - HTML content to display
     */
    setSeeAlsoContents(htmlContents) {
        const seeAlsoDiv = document.getElementById(ID_YELLOW_SEE_ALSO);
        const seeAlsoContent = seeAlsoDiv?.querySelector('.' + CLASS_SEE_ALSO_CONTENT);
        if (!seeAlsoDiv || !seeAlsoContent) return;

        seeAlsoContent.innerHTML = htmlContents;
        seeAlsoDiv.style.display = 'block';
        // Set up click handlers for the links
        this.setupSeeAlsoHandlers(seeAlsoDiv);
    }

    
    /**
     * Set up click handlers for "See Also" links
     * @param {HTMLElement} container - The See Also container element
     */
    setupSeeAlsoHandlers(container) {
        // Remove existing handlers to prevent duplicates
        const oldLinks = container.querySelectorAll('.' + CLASS_YELLOW_SEE_ALSO_LINK);
        oldLinks.forEach(link => {
            link.replaceWith(link.cloneNode(true));
        });

        // Add new handlers
        const links = container.querySelectorAll('.' + CLASS_YELLOW_SEE_ALSO_LINK);
        links.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const category = e.target.dataset.category;
                if (category) {
                    // Update the select dropdown
                    const yellowSelect = document.getElementById(DOM_ELEMENTS.ID_YELLOW_SEARCH_SELECT);
                    if (yellowSelect) {
                        yellowSelect.value = category;
                    }
                    // Trigger the search programmatically
                    this.mapApp.programmaticallyRunSearchOnQuery(SEARCH_TYPES.YELLOW, category, false);
                }
            });
        });
    }



    // ==========================================
    // Drop down populator
    // ==========================================


    /**
     * Populate the Yellow Pages dropdown with categories from pcatgdict
     * @param {Object} pcatgdict - Dictionary of place categories with keys as category names
     */
    populateCategories(pcatgdict) {
        if (!pcatgdict) {
            return;
        }
        const optionDisableParentCategories = false;
        const yellowSelect = document.getElementById(DOM_ELEMENTS.ID_YELLOW_SEARCH_SELECT);
        if (!yellowSelect) {
            console.error('ERROR: Yellow Pages select element not found');
            return;
        }

        if (!pcatgdict || typeof pcatgdict !== 'object') {
            console.error('ERROR: Invalid pcatgdict provided to populateCategories');
            return;
        }

        // Clear existing options except the first placeholder
        yellowSelect.innerHTML = '<option value="">Select a category...</option>';

        // Get sorted category keys
        const keyStringList = Object.keys(pcatgdict);

        // Custom sort: treat strings with '>' but not starting with '~' as if they start with '~'
        const categories = keyStringList.sort((a, b) => {
            const transformedA = (a.includes('>')) ? 'ZZZ~' + a : a;
            const transformedB = (b.includes('>')) ? 'ZZZ~' + b : b;
            return transformedA.localeCompare(transformedB);
        });

        // Track parent categories to avoid duplicates
        const addedParents = new Set();

        // Add options for each category
        categories.forEach(category => {
            const pctagEntry = pcatgdict[category];
            if ("yellowPages" in pctagEntry && pctagEntry.yellowPages === false) {
                return;
            }

            // Check if category contains '>' separator
            if (category.includes('>')) {
                const parts = category.split('>');
                const parent = parts[0].trim();
                const child = parts[1].trim();

                // Add parent category if not already added
                if (!addedParents.has(parent)) {
                    const parentOption = document.createElement('option');
                    parentOption.value = parent;
                    parentOption.textContent = parent.replace(/^[\s~]+/, '');
                    parentOption.disabled = optionDisableParentCategories;
                    parentOption.style.fontWeight = 'bold';
                    yellowSelect.appendChild(parentOption);
                    addedParents.add(parent);
                }

                // Add child category (indented)
                const childOption = document.createElement('option');
                childOption.value = category;
                childOption.textContent = '\u00A0\u00A0\u00A0\u00A0' + child;
                yellowSelect.appendChild(childOption);
            } else {
                // Regular category without hierarchy
                const option = document.createElement('option');
                option.value = category;
                option.textContent = category;
                yellowSelect.appendChild(option);
            }
        });
    }


    /**
     * Appends the SPECIAL group to the bottom of the Yellow Pages dropdown.
     * Adds a bold disabled "SPECIAL" parent option followed by one indented
     * child option per curated list. Each child uses a sentinel value so the
     * search code path can detect it and bypass the normal DB search.
     * Safe to call with no data; in that case no SPECIAL group is added.
     * @param {Object|null} specialYellowData - { listId: { label, items } }
     */
    addSpecialYellowPageSections(specialYellowData) {
        if (!specialYellowData) {
            return;
        }
        const entries = Object.entries(specialYellowData);
        if (entries.length === 0) {
            return;
        }
        const yellowSelect = document.getElementById(DOM_ELEMENTS.ID_YELLOW_SEARCH_SELECT);
        if (!yellowSelect) {
            return;
        }

        // SPECIAL parent (bold, non-selectable)
        const parentOption = document.createElement('option');
        parentOption.value = '';
        parentOption.textContent = 'SPECIAL';
        parentOption.disabled = true;
        parentOption.style.fontWeight = 'bold';
        yellowSelect.appendChild(parentOption);

        // Children (indented to match the existing parent>child rendering)
        for (const [listId, entry] of entries) {
            const childOption = document.createElement('option');
            childOption.value = SPECIAL_QUERY_PREFIX + listId;
            childOption.textContent = '    ' + (entry.label || listId);
            yellowSelect.appendChild(childOption);
        }
    }


    /**
     * Returns true if the given query string targets a SPECIAL list rather
     * than a real pcatg category.
     * @param {string} query
     * @returns {boolean}
     */
    isSpecialQuery(query) {
        return typeof query === 'string' && query.startsWith(SPECIAL_QUERY_PREFIX);
    }


    /**
     * Extracts the list id from a SPECIAL sentinel query.
     * @param {string} query
     * @returns {string}
     */
    getSpecialListIdFromQuery(query) {
        return query.slice(SPECIAL_QUERY_PREFIX.length);
    }




    
    // ==========================================
    // Search method
    // ==========================================


    /**
     * Override of DbSearcher.performSearchWork that intercepts SPECIAL list
     * sentinel queries and renders the curated items directly, bypassing the
     * POI database search entirely. Falls through to the base class for
     * normal pcatg category searches.
     * @param {Object} searchPack - The search pack with query and options
     * @override
     */
    async performSearchWork(searchPack) {
        if (this.isSpecialQuery(searchPack.query)) {
            await this.performSpecialListSearchWork(searchPack);
            return;
        }
        return super.performSearchWork(searchPack);
    }


    /**
     * Renders a SPECIAL curated list as if it were a search result.
     * Pulls pre-resolved items from MapApp.specialYellowData, builds a
     * synthetic search result, hands it to the lister, and updates the
     * map result layer. Skips history saving (handled in onSearchComplete
     * via the queryOriginal label so it does not show as the sentinel).
     * @param {Object} searchPack
     * @private
     */
    async performSpecialListSearchWork(searchPack) {
        const listId = this.getSpecialListIdFromQuery(searchPack.query);
        const specialData = this.mapApp.getSpecialYellowData();
        // the list id comes from the query text (stored history, imports), so own keys only
        const entry = getOwnValue(specialData, listId);
        const items = (entry && Array.isArray(entry.items)) ? entry.items : [];

        const searchResult = buildSearchResult(items, items.length);
        updateSearchPackWithSearchResult(searchPack, searchResult);

        this.lister.setAllResultsFromSearchPack(searchResult, searchPack);
        this.updateMapResultLayer(searchResult.results);
    }


    /**
     * Skip history saving for SPECIAL list queries — they aren't real
     * searches, and a sentinel-valued history entry would be both unsightly
     * and unrunnable from the History tab.
     * @param {Object} searchPack
     * @override
     */
    performPostSearchOperations(searchPack) {
        if (this.isSpecialQuery(searchPack.query)) {
            return;
        }
        super.performPostSearchOperations(searchPack);
    }


    /**
     * Searches for items by place category (pcatg).
     * Uses the generic searchDataWithFieldMatchFunction with an EXACT match on the category,
     * which also accepts a composite value by ANY of its '|'-separated categories: a place filed
     * as "~Off Map>New Jersey|Transport Depots / Airports / Terminals" is listed under both
     * "~Off Map>New Jersey" and "Transport Depots / Airports / Terminals"
     * (nynfuncs.calcCategorySegments). Its icon comes from the last segment only
     * (nynfuncs.calcCategoryTail, via the icon lookup). Values arrive lowercased, as does the query.
     * @param {Object} searchPack - The search pack with query (category name) and options
     * @returns {Promise<Object>} Search result object with results array and totalCount
     */
    async doYellowSearchForPcatg(searchPack) {
        // show loading right before real work?
        // await this.showLoading();

        // normalize search always
        searchPack.query = searchPack.query.trim();
        //
        //const matchFn = (value, searchPack, field) => value.includes(searchPack.query);
        const matchFn = (value, searchPack, field) => (value == searchPack.query) || calcCategorySegments(value).includes(searchPack.query);
        return this.searchDataWithFieldMatchFunction(
            searchPack,
            matchFn
        );
    }



    /**
     * Converts the current yellow pages search into an equivalent advanced search query.
     * Builds a pcatg includes expression and runs it as an advanced search.
     * SPECIAL list queries don't map to a pcatg expression, so this is a no-op for them.
     * @override
     */
    makeAdvancedSearch() {
        const searchPack = this.getLastSearchPack();
        const thisQueryStr = searchPack.query;
        if (!thisQueryStr) {
            return;
        }
        if (this.isSpecialQuery(thisQueryStr)) {
            return;
        }
        //const searchQuery = `((pcatg includes "${thisQueryStr}") or (pcat includes "${thisQueryStr}"))`;
        const searchQuery = `(pcatg includes "${thisQueryStr}")`;
        const searchType = SEARCH_TYPES.ADVANCED;
        this.mapApp.programmaticallyRunSearchOnQuery(searchType, searchQuery, false);
    }


    /**
     * Sorts results for yellow pages display by name.
     * SPECIAL lists are manually curated and keep their authored order without anything here: they
     * never reach a sort (performSearchWork sends them to performSpecialListSearchWork, which hands
     * them to the lister directly). A check here used to skip sorting when the LAST search pack was
     * a special list -- but this runs before the new search's pack is stored, so the check saw the
     * previous search, and the first category opened after a special list came back unsorted,
     * with its parent/child grouping broken (second code study 12.4, Oct 2026).
     * @param {Array} results - The results to sort
     * @returns {Array} The sorted results
     * @override
     */
    sortResults(results) {
        return this.sortResultsByField(results, 'sort_n', 'dName');
    }
}
